<template>
  <div class="tools-page">
    <base-header class="pb-6 pb-7 pt-5 pt-md-7 bg-gradient">
      <h2 class="page-title">{{ $t('tools.title') }}</h2>
      <p class="page-subtitle">{{ $t('tools.subtitle') }}</p>
    </base-header>

    <b-container fluid class="mt--7 page-content">
      <!-- ============ TRANSFER (wide) ============ -->
      <b-row>
        <b-col cols="12" class="mb-4">
          <div class="tool-card tool-card--wide" :class="{ 'tool-card--has-result': !!results.transfer }">
            <div class="tool-card__head">
              <div class="tool-card__icon tool-card__icon--primary">
                <i class="ni ni-send"></i>
              </div>
              <div class="tool-card__title-wrap">
                <h3 class="tool-card__title">{{ $t('tools.transfer.cardTitle') }}</h3>
                <p class="tool-card__hint">{{ $t('tools.transfer.cardHint') }}</p>
              </div>
              <div class="tool-card__head-meta">
                <span v-if="selectedProviderInfo" class="head-tag">
                  <BrandLogo :type="selectedProviderInfo.type" size="sm" />
                  <span>{{ selectedProviderInfo.type }} <span class="muted">#{{ selectedProviderInfo.id }}</span></span>
                </span>
                <span v-else class="head-tag head-tag--auto">
                  <span class="head-tag__auto">{{ $t('tools.transfer.autoRoundRobin') }}</span>
                  <span>{{ $t('tools.transfer.autoCaption') }}</span>
                </span>
              </div>
            </div>

            <div class="transfer-split">
              <!-- LEFT: form -->
              <div class="transfer-form">
                <div class="form-group">
                  <label class="form-label">{{ $t('tools.transfer.recipientCard') }}</label>
                  <div class="input-with-brand">
                    <input
                      type="text"
                      class="form-control form-control--lg"
                      placeholder="0000 0000 0000 0000"
                      maxlength="19"
                      v-model="transferForm.cardNumberDisplay"
                      @input="onTransferCardInput"
                    />
                    <BrandLogo
                      v-if="detectScheme(transferForm.card_number)"
                      class="input-brand"
                      :type="detectScheme(transferForm.card_number)"
                      size="sm"
                    />
                  </div>
                </div>

                <div class="form-group">
                  <label class="form-label">{{ $t('tools.transfer.amountUzs') }}</label>
                  <input
                    type="text"
                    class="form-control form-control--lg form-control--amount"
                    :placeholder="$t('tools.transfer.amountPlaceholder')"
                    v-model="transferForm.amountDisplay"
                    @input="onAmountInput"
                  />
                  <span class="form-hint">{{ $t('tools.transfer.amountMin') }}</span>
                </div>

                <div class="form-group">
                  <label class="form-label">{{ $t('tools.transfer.cheque') }}</label>
                  <input
                    type="text"
                    class="form-control"
                    :placeholder="$t('tools.transfer.chequePlaceholder')"
                    v-model="transferForm.cheque"
                  />
                </div>

                <button
                  type="button"
                  class="btn-action btn-action--primary btn-action--lg"
                  :disabled="!canTransfer || loading.transfer"
                  @click="runTransfer"
                >
                  <span v-if="loading.transfer" class="spinner"></span>
                  <i v-else class="ni ni-send mr-2"></i>
                  <span>{{ loading.transfer ? $t('tools.transfer.sending') : $t('tools.transfer.execute') }}</span>
                </button>
              </div>

              <!-- RIGHT: provider grid -->
              <div class="transfer-providers">
                <div class="transfer-providers__head">
                  <h4 class="transfer-providers__title">
                    <i class="ni ni-vector mr-2"></i>
                    {{ $t('tools.transfer.providerLabel') }}
                  </h4>
                  <span class="transfer-providers__hint">{{ $t('tools.transfer.providersActive', { count: providers.length }) }}</span>
                </div>

                <div class="provider-grid">
                  <button
                    type="button"
                    class="provider-tile provider-tile--auto"
                    :class="{ 'is-active': transferForm.provider_id === null }"
                    @click="transferForm.provider_id = null"
                  >
                    <div class="provider-tile__logo provider-tile__logo--auto">
                      <i class="ni ni-atom"></i>
                    </div>
                    <div class="provider-tile__name">{{ $t('tools.transfer.auto') }}</div>
                    <div class="provider-tile__id">{{ $t('tools.transfer.autoCaption') }}</div>
                    <i v-if="transferForm.provider_id === null" class="provider-tile__check ni ni-check-bold"></i>
                  </button>

                  <button
                    v-for="p in providers"
                    :key="p.id"
                    type="button"
                    class="provider-tile"
                    :class="{ 'is-active': transferForm.provider_id === p.id, 'is-inactive': !p.is_active }"
                    @click="transferForm.provider_id = p.id"
                  >
                    <BrandLogo :type="p.type" size="lg" class="provider-tile__logo" />
                    <div class="provider-tile__name">{{ p.type }}</div>
                    <div class="provider-tile__id">#{{ p.id }}</div>
                    <i v-if="transferForm.provider_id === p.id" class="provider-tile__check ni ni-check-bold"></i>
                  </button>

                  <div v-if="providers.length === 0" class="provider-empty">
                    <i class="ni ni-cloud-download-95"></i>
                    <span>{{ $t('tools.transfer.loadingProviders') }}</span>
                  </div>
                </div>

                <!-- Sender card picker (active cards bound to chosen provider) -->
                <div v-if="transferForm.provider_id" class="sender-picker">
                  <div class="sender-picker__head">
                    <h4 class="sender-picker__title">
                      <i class="ni ni-credit-card mr-2"></i>
                      {{ $t('tools.transfer.senderCard') }}
                    </h4>
                    <span class="sender-picker__hint">
                      <span v-if="senderCards.loading" class="sender-picker__loading-dot"></span>
                      <span v-if="senderCards.loading">{{ $t('tools.transfer.loadingCards') }}</span>
                      <span v-else-if="senderCards.error" class="sender-picker__err">{{ senderCards.error }}</span>
                      <span v-else>{{ $t('tools.transfer.providersActive', { count: senderCards.list.length }) }}</span>
                    </span>
                  </div>

                  <div v-if="!senderCards.loading && !senderCards.error" class="sender-cards">
                    <!-- AUTO sender card -->
                    <button
                      type="button"
                      class="mini-card mini-card--auto"
                      :class="{ 'is-active': transferForm.s_card_id === null }"
                      @click="transferForm.s_card_id = null"
                    >
                      <div class="mini-card__top">
                        <span class="mini-card__brand">{{ $t('tools.transfer.autoRoundRobin') }}</span>
                        <span class="mini-card__check" v-if="transferForm.s_card_id === null">
                          <i class="ni ni-check-bold"></i>
                        </span>
                      </div>
                      <div class="mini-card__chip mini-card__chip--auto">
                        <i class="ni ni-atom"></i>
                      </div>
                      <div class="mini-card__pan">•••• •••• •••• ••••</div>
                      <div class="mini-card__bottom">
                        <span class="mini-card__label">{{ $t('tools.transfer.autoLabel') }}</span>
                        <span class="mini-card__balance">{{ $t('tools.transfer.autoSubLabel') }}</span>
                      </div>
                    </button>

                    <!-- Real cards -->
                    <button
                      v-for="card in senderCards.list"
                      :key="card.id"
                      type="button"
                      class="mini-card"
                      :class="{ 'is-active': transferForm.s_card_id === card.id }"
                      @click="transferForm.s_card_id = card.id"
                    >
                      <div class="mini-card__top">
                        <BrandLogo
                          :type="schemeFromPan(card.pan || card.number)"
                          size="sm"
                        />
                        <span class="mini-card__check" v-if="transferForm.s_card_id === card.id">
                          <i class="ni ni-check-bold"></i>
                        </span>
                      </div>
                      <div class="mini-card__chip">
                        <svg width="32" height="22" viewBox="0 0 45 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="45" height="35" rx="6" fill="url(#chipGrad-mini)"/>
                          <rect x="8" y="8" width="10" height="18" fill="rgba(0,0,0,0.1)"/>
                          <rect x="27" y="8" width="10" height="18" fill="rgba(0,0,0,0.1)"/>
                          <rect x="4" y="17" width="37" height="1" fill="rgba(0,0,0,0.1)"/>
                          <defs>
                            <linearGradient id="chipGrad-mini" x1="0" y1="0" x2="45" y2="35" gradientUnits="userSpaceOnUse">
                              <stop stop-color="#FFFCB1"/>
                              <stop offset="1" stop-color="#B4A365"/>
                            </linearGradient>
                          </defs>
                        </svg>
                      </div>
                      <div class="mini-card__pan">{{ formatMiniPan(card.pan || card.number) }}</div>
                      <div class="mini-card__bottom">
                        <span class="mini-card__label">{{ $t('tools.transfer.balanceLabel') }}</span>
                        <span class="mini-card__balance">{{ formatBalance(card.balance) }}</span>
                      </div>
                    </button>

                    <div v-if="senderCards.list.length === 0" class="sender-cards__empty">
                      <i class="ni ni-fat-remove"></i>
                      <span>{{ $t('tools.transfer.noCardsForProvider') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Inline transfer result -->
            <transition name="result-fade">
              <div v-if="results.transfer" class="result-panel" :class="'result-panel--' + results.transfer.status">
                <!-- Success -->
                <template v-if="results.transfer.status === 'success'">
                  <div class="result-panel__icon">
                    <i class="ni ni-check-bold"></i>
                  </div>
                  <div class="result-panel__body">
                    <div class="result-panel__row">
                      <span class="result-panel__label">{{ $t('tools.transfer.transferDone') }}</span>
                      <span class="result-panel__amount">{{ results.transfer.amount }}</span>
                    </div>
                    <div class="result-panel__row result-panel__row--meta">
                      <div class="result-meta">
                        <span class="result-meta__label">{{ $t('tools.transfer.recipient') }}</span>
                        <span class="result-meta__value mono">{{ results.transfer.cardMasked }}</span>
                      </div>
                      <div class="result-meta">
                        <span class="result-meta__label">{{ $t('tools.transfer.providerLabel') }}</span>
                        <span class="result-meta__value with-brand">
                          <BrandLogo v-if="results.transfer.providerType" :type="results.transfer.providerType" size="sm" />
                          {{ results.transfer.providerType || '—' }}
                        </span>
                      </div>
                      <div class="result-meta">
                        <span class="result-meta__label">{{ $t('tools.transfer.chequeLabel') }}</span>
                        <span class="result-meta__value mono">{{ results.transfer.cheque }}</span>
                      </div>
                      <div v-if="results.transfer.token" class="result-meta">
                        <span class="result-meta__label">{{ $t('tools.transfer.tokenLabel') }}</span>
                        <span class="result-meta__value mono result-meta__value--copy" @click="copyText(results.transfer.token)">
                          {{ shortToken(results.transfer.token) }} <i class="ni ni-single-copy-04"></i>
                        </span>
                      </div>
                    </div>
                    <div v-if="results.transfer.fileUrl" class="result-panel__actions">
                      <a :href="results.transfer.fileUrl" target="_blank" class="result-link">
                        <i class="ni ni-cloud-download-95 mr-1"></i> {{ $t('tools.transfer.downloadCheque') }}
                      </a>
                    </div>
                  </div>
                </template>

                <!-- SMS pending -->
                <template v-else-if="results.transfer.status === 'sms-pending'">
                  <div class="result-panel__icon result-panel__icon--warn">
                    <i class="ni ni-bell-55"></i>
                  </div>
                  <div class="result-panel__body">
                    <div class="result-panel__row">
                      <span class="result-panel__label">{{ $t('tools.transfer.smsRequired') }}</span>
                    </div>
                    <div class="result-panel__row result-panel__row--meta">
                      <div class="result-meta">
                        <span class="result-meta__label">{{ $t('tools.sms.amountLabel') }}</span>
                        <span class="result-meta__value">{{ results.transfer.amount }}</span>
                      </div>
                      <div class="result-meta">
                        <span class="result-meta__label">{{ $t('tools.transfer.recipient') }}</span>
                        <span class="result-meta__value mono">{{ results.transfer.cardMasked }}</span>
                      </div>
                      <div class="result-meta">
                        <span class="result-meta__label">{{ $t('tools.transfer.providerLabel') }}</span>
                        <span class="result-meta__value with-brand">
                          <BrandLogo v-if="results.transfer.providerType" :type="results.transfer.providerType" size="sm" />
                          {{ results.transfer.providerType || '—' }}
                        </span>
                      </div>
                    </div>
                    <div class="result-panel__actions">
                      <button type="button" class="result-link" @click="reopenSmsModal">
                        <i class="ni ni-mobile-button mr-1"></i> {{ $t('tools.transfer.openSmsForm') }}
                      </button>
                    </div>
                  </div>
                </template>

                <!-- Error -->
                <template v-else>
                  <div class="result-panel__icon result-panel__icon--err">
                    <i class="ni ni-fat-remove"></i>
                  </div>
                  <div class="result-panel__body">
                    <div class="result-panel__row">
                      <span class="result-panel__label">{{ $t('tools.transfer.transferError') }}</span>
                    </div>
                    <div class="result-panel__error">{{ results.transfer.error }}</div>
                    <div v-if="results.transfer.engagedCard" class="result-meta">
                      <span class="result-meta__label">{{ $t('tools.transfer.engagedCard') }}</span>
                      <span class="result-meta__value mono">{{ results.transfer.engagedCard }}</span>
                    </div>
                  </div>
                </template>

                <button type="button" class="result-panel__close" @click="results.transfer = null" :title="$t('tools.transfer.hide')">
                  <i class="ni ni-fat-remove"></i>
                </button>
              </div>
            </transition>
          </div>
        </b-col>
      </b-row>

      <!-- ============ PHONE + HOLDER ============ -->
      <b-row>
        <!-- Phone -->
        <b-col xl="6" lg="6" class="mb-4">
          <div class="tool-card" :class="{ 'tool-card--has-result': !!results.phone }">
            <div class="tool-card__head">
              <div class="tool-card__icon tool-card__icon--cyan">
                <i class="ni ni-mobile-button"></i>
              </div>
              <div class="tool-card__title-wrap">
                <h3 class="tool-card__title">{{ $t('tools.phone.cardTitle') }}</h3>
                <p class="tool-card__hint">{{ $t('tools.phone.cardHint') }}</p>
              </div>
            </div>

            <div class="tool-card__body">
              <div class="form-group">
                <label class="form-label">{{ $t('tools.phone.phoneNumber') }}</label>
                <div class="input-with-prefix">
                  <span class="input-prefix">+</span>
                  <input
                    type="text"
                    class="form-control form-control--phone"
                    placeholder="998 90 123 45 67"
                    v-model="phoneForm.phoneDisplay"
                    @input="onPhoneInput"
                  />
                </div>
                <span class="form-hint">{{ $t('tools.phone.phoneFormat') }}</span>
              </div>

              <div class="form-group">
                <label class="form-label">{{ $t('tools.phone.dataSource') }}</label>
                <div class="info-row">
                  <BrandLogo type="apelsin" size="sm" />
                  <span class="info-row__text">{{ $t('tools.phone.dataSourceText') }}</span>
                </div>
              </div>

              <div class="form-group">
                <label class="checkbox-row">
                  <input type="checkbox" v-model="phoneForm.force_refresh" />
                  <span>{{ $t('tools.phone.ignoreCache') }}</span>
                </label>
              </div>
            </div>

            <div class="tool-card__foot">
              <button
                type="button"
                class="btn-action btn-action--cyan"
                :disabled="!canPhoneLookup || loading.phone"
                @click="runPhoneLookup"
              >
                <span v-if="loading.phone" class="spinner"></span>
                <i v-else class="ni ni-zoom-split-in mr-2"></i>
                <span>{{ loading.phone ? $t('tools.phone.searching') : $t('tools.phone.search') }}</span>
              </button>
            </div>

            <!-- Inline phone result -->
            <transition name="result-fade">
              <div v-if="results.phone" class="result-panel" :class="'result-panel--' + results.phone.status">
                <template v-if="results.phone.status === 'success'">
                  <div class="result-panel__head">
                    <div class="result-panel__head-left">
                      <i class="ni ni-check-bold result-panel__head-icon"></i>
                      <span class="result-panel__head-title">{{ $t('tools.phone.found', { count: results.phone.count }) }}</span>
                      <span v-if="results.phone.cache" class="cache-badge">{{ $t('tools.phone.cache') }}</span>
                    </div>
                    <button type="button" class="result-panel__close" @click="results.phone = null"><i class="ni ni-fat-remove"></i></button>
                  </div>
                  <div class="phone-cards">
                    <div
                      v-for="(card, i) in results.phone.cards"
                      :key="card.card_id || i"
                      class="phone-card"
                      :class="'phone-card--' + schemeFromCardType(card.card_type)"
                    >
                      <div class="phone-card__top">
                        <BrandLogo :type="schemeFromCardType(card.card_type)" size="sm" />
                        <span v-if="card.bank_name" class="phone-card__bank">{{ card.bank_name }}</span>
                      </div>
                      <div class="phone-card__chip">
                        <svg width="32" height="22" viewBox="0 0 45 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="45" height="35" rx="6" fill="url(#chipGrad-phone)"/>
                          <rect x="8" y="8" width="10" height="18" fill="rgba(0,0,0,0.18)"/>
                          <rect x="27" y="8" width="10" height="18" fill="rgba(0,0,0,0.18)"/>
                          <rect x="4" y="17" width="37" height="1" fill="rgba(0,0,0,0.18)"/>
                          <defs>
                            <linearGradient id="chipGrad-phone" x1="0" y1="0" x2="45" y2="35" gradientUnits="userSpaceOnUse">
                              <stop stop-color="#FFFCB1"/>
                              <stop offset="1" stop-color="#B4A365"/>
                            </linearGradient>
                          </defs>
                        </svg>
                      </div>
                      <div class="phone-card__pan">{{ prettyMaskedPan(card.masked_pan || card.card_id) }}</div>
                      <div class="phone-card__bottom">
                        <span class="phone-card__label">{{ $t('tools.phone.owner') }}</span>
                        <span class="phone-card__name">{{ card.recipient_short_name || '—' }}</span>
                      </div>
                    </div>
                    <div v-if="!results.phone.cards.length" class="phone-empty">
                      {{ $t('tools.phone.noCards') }}
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="result-panel__head">
                    <div class="result-panel__head-left">
                      <i class="ni ni-fat-remove result-panel__head-icon result-panel__head-icon--err"></i>
                      <span class="result-panel__head-title">{{ $t('tools.phone.errorTitle') }}</span>
                    </div>
                    <button type="button" class="result-panel__close" @click="results.phone = null"><i class="ni ni-fat-remove"></i></button>
                  </div>
                  <div class="result-panel__error">{{ results.phone.error }}</div>
                </template>
              </div>
            </transition>
          </div>
        </b-col>

        <!-- Holder -->
        <b-col xl="6" lg="6" class="mb-4">
          <div class="tool-card" :class="{ 'tool-card--has-result': !!results.holder }">
            <div class="tool-card__head">
              <div class="tool-card__icon tool-card__icon--green">
                <i class="ni ni-single-02"></i>
              </div>
              <div class="tool-card__title-wrap">
                <h3 class="tool-card__title">{{ $t('tools.holder.cardTitle') }}</h3>
                <p class="tool-card__hint">{{ $t('tools.holder.cardHint') }}</p>
              </div>
            </div>

            <div class="tool-card__body">
              <div class="form-group">
                <label class="form-label">{{ $t('tools.holder.cardNumber') }}</label>
                <div class="input-with-brand">
                  <input
                    type="text"
                    class="form-control"
                    placeholder="0000 0000 0000 0000"
                    maxlength="19"
                    v-model="holderForm.cardNumberDisplay"
                    @input="onHolderCardInput"
                  />
                  <BrandLogo
                    v-if="detectScheme(holderForm.card_number)"
                    class="input-brand"
                    :type="detectScheme(holderForm.card_number)"
                    size="sm"
                  />
                </div>
                <span class="form-hint">{{ $t('tools.holder.schemeHint') }}</span>
              </div>

              <div class="form-group">
                <label class="form-label">{{ $t('tools.holder.binsLabel') }}</label>
                <div class="bin-chips">
                  <div
                    v-for="bin in bins"
                    :key="bin.scheme"
                    class="bin-chip"
                    :class="{ 'is-active': detectScheme(holderForm.card_number) === bin.scheme }"
                  >
                    <BrandLogo :type="bin.scheme" size="sm" />
                    <span class="bin-chip__code">{{ bin.code }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="tool-card__foot">
              <button
                type="button"
                class="btn-action btn-action--green"
                :disabled="!canHolderLookup || loading.holder"
                @click="runHolderLookup"
              >
                <span v-if="loading.holder" class="spinner"></span>
                <i v-else class="ni ni-check-bold mr-2"></i>
                <span>{{ loading.holder ? $t('tools.holder.verifying') : $t('tools.holder.verify') }}</span>
              </button>
            </div>

            <!-- Inline holder result -->
            <transition name="result-fade">
              <div v-if="results.holder" class="result-panel" :class="'result-panel--' + results.holder.status">
                <template v-if="results.holder.status === 'success'">
                  <div class="result-panel__head">
                    <div class="result-panel__head-left">
                      <i class="ni ni-check-bold result-panel__head-icon"></i>
                      <span class="result-panel__head-title">{{ $t('tools.holder.found') }}</span>
                      <span v-if="results.holder.cache" class="cache-badge">{{ $t('tools.phone.cache') }}</span>
                    </div>
                    <button type="button" class="result-panel__close" @click="results.holder = null"><i class="ni ni-fat-remove"></i></button>
                  </div>
                  <div class="holder-result">
                    <div class="holder-result__name">{{ results.holder.fullName }}</div>
                    <div class="holder-result__meta">
                      <span class="holder-result__pill">
                        <BrandLogo v-if="results.holder.scheme" :type="results.holder.scheme" size="sm" />
                        <span>{{ results.holder.cardMasked }}</span>
                      </span>
                      <span v-if="results.holder.currency" class="holder-result__pill holder-result__pill--muted">
                        {{ results.holder.currency }}
                      </span>
                      <span v-if="results.holder.cardType" class="holder-result__pill holder-result__pill--muted">
                        {{ results.holder.cardType }}
                      </span>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="result-panel__head">
                    <div class="result-panel__head-left">
                      <i class="ni ni-fat-remove result-panel__head-icon result-panel__head-icon--err"></i>
                      <span class="result-panel__head-title">{{ $t('tools.holder.notVerified') }}</span>
                    </div>
                    <button type="button" class="result-panel__close" @click="results.holder = null"><i class="ni ni-fat-remove"></i></button>
                  </div>
                  <div class="result-panel__error">{{ results.holder.error }}</div>
                </template>
              </div>
            </transition>
          </div>
        </b-col>
      </b-row>

      <!-- ============ AUDIT LOG ============ -->
      <b-row>
        <b-col>
          <div class="audit-card">
            <div class="audit-card__head">
              <div class="audit-card__head-left">
                <h2 class="audit-card__title">
                  <i class="ni ni-bullet-list-67 mr-2"></i>
                  {{ $t('tools.auditTitle') }}
                </h2>
                <p class="audit-card__hint">
                  <span>{{ $t('tools.logEntries', { count: logEntries.length }) }}</span>
                  <span class="audit-card__hint-sep">·</span>
                  <span>{{ $t('tools.fromCache', { count: cacheHits }) }}</span>
                  <span class="audit-card__hint-sep">·</span>
                  <span :class="{ 'audit-card__hint-err': errorsCount > 0 }">{{ $t('tools.errorsCount', { count: errorsCount }) }}</span>
                </p>
              </div>

              <div class="audit-card__filters">
                <button
                  v-for="f in filters"
                  :key="f.value"
                  type="button"
                  class="filter-chip"
                  :class="{ 'is-active': activeFilter === f.value }"
                  @click="activeFilter = f.value"
                >
                  <i v-if="f.icon" :class="f.icon"></i>
                  <span>{{ f.label }}</span>
                  <span class="filter-chip__count">{{ f.count }}</span>
                </button>
              </div>

              <div class="audit-card__tools">
                <button type="button" class="audit-tool-btn" @click="exportLog" :title="$t('tools.audit.exportJson')">
                  <i class="ni ni-cloud-download-95"></i>
                </button>
                <button type="button" class="audit-tool-btn audit-tool-btn--danger" @click="clearLog" :title="$t('tools.audit.clear')">
                  <i class="ni ni-fat-remove"></i>
                </button>
              </div>
            </div>

            <div class="audit-card__body">
              <transition-group name="audit-list" tag="div" class="audit-list">
                <div
                  v-for="entry in filteredEntries"
                  :key="entry.id"
                  class="audit-entry"
                  :class="['audit-entry--' + entry.status, 'audit-entry--' + entry.kind]"
                  @click="toggleEntry(entry.id)"
                >
                  <div class="audit-entry__row">
                    <div class="audit-entry__left">
                      <div class="audit-entry__icon">
                        <i :class="kindIcon(entry.kind)"></i>
                      </div>
                      <div class="audit-entry__meta">
                        <div class="audit-entry__title">
                          <span>{{ kindLabel(entry.kind) }}</span>
                          <BrandLogo
                            v-if="entry.brand"
                            :type="entry.brand"
                            size="sm"
                            class="ml-2"
                          />
                          <span v-if="entry.cache" class="cache-badge">кэш</span>
                        </div>
                        <div class="audit-entry__subtitle">{{ entry.summary }}</div>
                      </div>
                    </div>

                    <div class="audit-entry__right">
                      <span class="audit-entry__status-badge">
                        <span class="audit-entry__status-dot"></span>
                        {{ statusLabel(entry.status) }}
                      </span>
                      <span class="audit-entry__time">{{ formatTime(entry.timestamp) }}</span>
                      <i
                        class="audit-entry__chevron ni"
                        :class="expanded[entry.id] ? 'ni-bold-up' : 'ni-bold-down'"
                      ></i>
                    </div>
                  </div>

                  <div v-if="expanded[entry.id]" class="audit-entry__details">
                    <div class="detail-section">
                      <div class="detail-section__head"><i class="ni ni-curved-next mr-2"></i>{{ $t('tools.audit.request') }}</div>
                      <pre>{{ formatJson(entry.request) }}</pre>
                    </div>
                    <div class="detail-section">
                      <div class="detail-section__head"><i class="ni ni-bold-down mr-2"></i>{{ $t('tools.audit.response') }}</div>
                      <pre :class="{ 'is-error': entry.status === 'error' }">{{ formatJson(entry.response) }}</pre>
                    </div>
                    <div class="detail-meta">
                      <span><i class="ni ni-time-alarm mr-1"></i>{{ entry.duration }} {{ $t('tools.audit.ms') }}</span>
                      <span><i class="ni ni-key-25 mr-1"></i>{{ entry.id }}</span>
                    </div>
                  </div>
                </div>
              </transition-group>

              <div v-if="filteredEntries.length === 0" class="audit-empty">
                <i class="ni ni-archive-2"></i>
                <p>{{ logEntries.length === 0
                  ? $t('tools.audit.empty')
                  : $t('tools.audit.emptyFilter') }}
                </p>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>

    <!-- ============ SMS MODAL ============ -->
    <b-modal
      v-model="smsModal.visible"
      modal-class="sms-modal"
      hide-footer
      hide-header
      no-close-on-backdrop
      no-close-on-esc
      centered
      size="md"
    >
      <div v-if="smsModal.context" class="sms-modal__inner">
        <div class="sms-modal__head">
          <div class="sms-modal__head-icon"><i class="ni ni-mobile-button"></i></div>
          <div>
            <h3 class="sms-modal__title">{{ $t('tools.sms.title') }}</h3>
            <p class="sms-modal__hint">{{ $t('tools.sms.hint') }}</p>
          </div>
          <button type="button" class="sms-modal__close" @click="closeSmsModal" :disabled="smsModal.loading">
            <i class="ni ni-fat-remove"></i>
          </button>
        </div>

        <div class="sms-modal__transfer">
          <div class="sms-transfer__row sms-transfer__row--main">
            <span class="sms-transfer__amount">{{ smsModal.context.amountDisplay }}</span>
            <span class="sms-transfer__arrow"><i class="ni ni-bold-right"></i></span>
            <span class="sms-transfer__card mono">{{ smsModal.context.cardMasked }}</span>
          </div>
          <div class="sms-transfer__row">
            <div class="sms-transfer__cell">
              <span class="sms-transfer__label">{{ $t('tools.sms.providerLabel') }}</span>
              <span class="sms-transfer__value with-brand">
                <BrandLogo v-if="smsModal.context.providerType" :type="smsModal.context.providerType" size="sm" />
                {{ smsModal.context.providerType || '—' }}
              </span>
            </div>
            <div class="sms-transfer__cell">
              <span class="sms-transfer__label">{{ $t('tools.sms.chequeLabel') }}</span>
              <span class="sms-transfer__value mono">{{ smsModal.context.cheque }}</span>
            </div>
            <div class="sms-transfer__cell">
              <span class="sms-transfer__label">{{ $t('tools.sms.expiresLabel') }}</span>
              <span class="sms-transfer__value mono"
                :class="{ 'sms-transfer__value--warn': smsCountdown <= 15 && smsCountdown > 0,
                          'sms-transfer__value--err': smsCountdown <= 0 }">
                {{ smsCountdown > 0 ? smsCountdown + ' ' + $t('tools.sms.secondsShort') : $t('tools.sms.expired') }}
              </span>
            </div>
          </div>
        </div>

        <div class="sms-modal__field">
          <label class="form-label">{{ $t('tools.sms.smsCode') }}</label>
          <input
            ref="smsInput"
            type="text"
            class="form-control sms-modal__input"
            placeholder="••••••"
            maxlength="8"
            inputmode="numeric"
            v-model="smsModal.code"
            @input="onSmsCodeInput"
            @keydown.enter="confirmSmsModal"
            :disabled="smsModal.loading"
          />
        </div>

        <div v-if="smsModal.error" class="sms-modal__error">
          <i class="ni ni-fat-remove mr-2"></i>{{ smsModal.error }}
        </div>

        <div class="sms-modal__actions">
          <button
            type="button"
            class="btn-action btn-action--ghost"
            @click="closeSmsModal"
            :disabled="smsModal.loading"
          >{{ $t('common.cancel') }}</button>
          <button
            type="button"
            class="btn-action btn-action--primary"
            :disabled="!canConfirmSms || smsModal.loading || smsCountdown <= 0"
            @click="confirmSmsModal"
          >
            <span v-if="smsModal.loading" class="spinner"></span>
            <i v-else class="ni ni-check-bold mr-2"></i>
            <span>{{ smsModal.loading ? $t('tools.sms.confirming') : $t('tools.sms.confirm') }}</span>
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import BrandLogo from '@/components/Tools/BrandLogo.vue';

const STORAGE_KEY = 'tools-audit-log-v1';
const MAX_LOG_ENTRIES = 200;

const BINS = [
  { scheme: 'uzcard',     code: '8600 / 6262' },
  { scheme: 'humo',       code: '9860' },
  { scheme: 'visa',       code: '4xxx' },
  { scheme: 'mastercard', code: '5xxx' },
];

const CARD_TYPE_TO_SCHEME = {
  uzcard: 'uzcard',
  humo:   'humo',
  visa:   'visa',
  mc:     'mastercard',
  mastercard: 'mastercard',
};

export default {
  name: 'Tools',
  components: { BrandLogo },
  data() {
    return {
      providers: [],

      transferForm: {
        cardNumberDisplay: '',
        card_number: '',
        amountDisplay: '',
        amount: 0,
        provider_id: null,
        s_card_id: null,
        cheque: '',
      },
      senderCards: {
        loading: false,
        error: null,
        list: [],
      },
      phoneForm: {
        phoneDisplay: '',
        phone_number: '',
        force_refresh: false,
      },
      holderForm: {
        cardNumberDisplay: '',
        card_number: '',
      },

      loading: { transfer: false, phone: false, holder: false },

      results: {
        transfer: null,
        phone: null,
        holder: null,
      },

      smsModal: {
        visible: false,
        loading: false,
        code: '',
        context: null,
        error: null,
      },
      smsCountdown: 0,
      smsTimer: null,

      logEntries: [],
      expanded: {},
      activeFilter: 'all',
      bins: BINS,
    };
  },

  computed: {
    canTransfer() {
      return this.transferForm.card_number.length === 16
        && this.transferForm.amount >= 5000;
    },
    canPhoneLookup() {
      return /^998\d{9}$/.test(this.phoneForm.phone_number);
    },
    canHolderLookup() {
      return this.holderForm.card_number.length === 16;
    },
    canConfirmSms() {
      return /^\d{3,8}$/.test(this.smsModal.code);
    },
    cacheHits() {
      return this.logEntries.filter(e => e.cache).length;
    },
    errorsCount() {
      return this.logEntries.filter(e => e.status === 'error').length;
    },
    selectedProviderInfo() {
      if (!this.transferForm.provider_id) return null;
      return this.providers.find(p => p.id === this.transferForm.provider_id) || null;
    },
    filters() {
      const counts = this.logEntries.reduce((acc, e) => {
        acc[e.kind] = (acc[e.kind] || 0) + 1;
        return acc;
      }, {});
      return [
        { label: this.$t('tools.audit.filterAll'),      value: 'all',      icon: 'ni ni-app',            count: this.logEntries.length },
        { label: this.$t('tools.audit.filterTransfer'), value: 'transfer', icon: 'ni ni-send',           count: counts.transfer || 0 },
        { label: this.$t('tools.audit.filterPhone'),    value: 'phone',    icon: 'ni ni-mobile-button',  count: counts.phone || 0 },
        { label: this.$t('tools.audit.filterHolder'),   value: 'holder',   icon: 'ni ni-single-02',      count: counts.holder || 0 },
        { label: this.$t('tools.audit.filterErrors'),   value: 'errors',   icon: 'ni ni-fat-remove',     count: this.errorsCount },
      ];
    },
    filteredEntries() {
      if (this.activeFilter === 'all') return this.logEntries;
      if (this.activeFilter === 'errors') return this.logEntries.filter(e => e.status === 'error');
      return this.logEntries.filter(e => e.kind === this.activeFilter);
    },
  },

  mounted() {
    this.loadLog();
    this.fetchProviders();
  },

  beforeDestroy() {
    if (this.smsTimer) clearInterval(this.smsTimer);
  },

  watch: {
    'transferForm.provider_id'(newId) {
      // Reset selected sender when provider changes
      this.transferForm.s_card_id = null;
      if (newId) {
        this.fetchSenderCards(newId);
      } else {
        this.senderCards.list = [];
        this.senderCards.error = null;
      }
    },
  },

  methods: {
    async fetchProviders() {
      try {
        const { data } = await axios.get('dashboard/tools/providers');
        this.providers = data.providers || [];
      } catch (e) {
        if (e.response && e.response.status === 401) {
          this.$router.push({ name: 'login' });
          return;
        }
        console.error(e);
      }
    },

    async fetchSenderCards(providerId) {
      this.senderCards.loading = true;
      this.senderCards.error = null;
      this.senderCards.list = [];
      try {
        const { data } = await axios.get('dashboard/tools/providers/' + providerId + '/cards');
        if (data && data.status) {
          this.senderCards.list = data.cards || [];
        } else {
          this.senderCards.error = (data && data.error) || 'Не удалось загрузить карты';
        }
      } catch (e) {
        if (e.response && e.response.status === 401) {
          this.$router.push({ name: 'login' });
          return;
        }
        this.senderCards.error = (e.response && e.response.data && e.response.data.error)
          || 'Ошибка загрузки карт';
      } finally {
        this.senderCards.loading = false;
      }
    },

    /* ===== Inputs ===== */
    onTransferCardInput(e) {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 16);
      this.transferForm.card_number = digits;
      this.transferForm.cardNumberDisplay = digits.replace(/(.{4})/g, '$1 ').trim();
    },
    onHolderCardInput(e) {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 16);
      this.holderForm.card_number = digits;
      this.holderForm.cardNumberDisplay = digits.replace(/(.{4})/g, '$1 ').trim();
    },
    onAmountInput(e) {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 12);
      this.transferForm.amount = digits ? parseInt(digits, 10) : 0;
      this.transferForm.amountDisplay = digits
        ? Number(digits).toLocaleString('ru-RU').replace(/,/g, ' ')
        : '';
    },
    onPhoneInput(e) {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 12);
      this.phoneForm.phone_number = digits;
      let pretty = digits;
      if (digits.length > 3) pretty = digits.slice(0, 3) + ' ' + digits.slice(3);
      if (digits.length > 5) pretty = digits.slice(0, 3) + ' ' + digits.slice(3, 5) + ' ' + digits.slice(5);
      if (digits.length > 8) pretty = digits.slice(0, 3) + ' ' + digits.slice(3, 5) + ' ' + digits.slice(5, 8) + ' ' + digits.slice(8);
      if (digits.length > 10) pretty = digits.slice(0, 3) + ' ' + digits.slice(3, 5) + ' ' + digits.slice(5, 8) + ' ' + digits.slice(8, 10) + ' ' + digits.slice(10);
      this.phoneForm.phoneDisplay = pretty;
    },
    onSmsCodeInput(e) {
      this.smsModal.code = e.target.value.replace(/\D/g, '').slice(0, 8);
      this.smsModal.error = null;
    },

    detectScheme(cardNumber) {
      const n = String(cardNumber || '').replace(/\D/g, '');
      if (!n) return '';
      if (n.startsWith('8600') || n.startsWith('6262')) return 'uzcard';
      if (n.startsWith('9860')) return 'humo';
      if (n.startsWith('4')) return 'visa';
      if (n.startsWith('5')) return 'mastercard';
      return '';
    },
    schemeFromCardType(t) {
      if (!t) return 'unknown';
      const k = String(t).toLowerCase().replace(/[^a-z]/g, '');
      return CARD_TYPE_TO_SCHEME[k] || 'unknown';
    },

    schemeFromPan(pan) {
      const n = String(pan || '').replace(/\D/g, '');
      if (!n) return 'unknown';
      if (n.startsWith('8600') || n.startsWith('6262')) return 'uzcard';
      if (n.startsWith('9860')) return 'humo';
      if (n.startsWith('4')) return 'visa';
      if (n.startsWith('5')) return 'mastercard';
      return 'unknown';
    },

    formatMiniPan(pan) {
      const s = String(pan || '').replace(/\s/g, '');
      if (s.length >= 8) return s.slice(0, 4) + ' •••• •••• ' + s.slice(-4);
      if (s.length >= 4) return s.slice(0, 4) + ' •••• •••• ••••';
      return '•••• •••• •••• ••••';
    },

    prettyMaskedPan(value) {
      const raw = String(value || '').trim();
      if (!raw) return '•••• •••• •••• ••••';
      const digitsOnly = raw.replace(/\D/g, '');
      // Already a fully formatted masked PAN (12+ chars with mask chars)
      if (raw.length > 8 && /[•*xX]/.test(raw)) return raw;
      // Last 4 digits only — pad with bullet groups
      if (digitsOnly.length <= 4 && raw === digitsOnly) {
        return '•••• •••• •••• ' + digitsOnly.padStart(4, '•');
      }
      return raw;
    },

    formatBalance(balance) {
      const v = Number(balance || 0) / 100;
      return v.toLocaleString('ru-RU').replace(/,/g, ' ') + ' UZS';
    },

    /* ===== Transfer ===== */
    async runTransfer() {
      if (!this.canTransfer) return;
      const payload = {
        card_number: this.transferForm.card_number,
        amount: this.transferForm.amount * 100,
      };
      if (this.transferForm.provider_id) payload.provider_id = this.transferForm.provider_id;
      if (this.transferForm.s_card_id) payload.s_card_id = this.transferForm.s_card_id;
      if (this.transferForm.cheque) payload.cheque = this.transferForm.cheque;

      this.loading.transfer = true;
      this.results.transfer = null;
      const t0 = performance.now();

      try {
        const { data } = await axios.post('dashboard/tools/transfer', payload);
        const ok = data && data.status === true;
        const providerType = (data && data.data && data.data.provider_type)
          || (this.selectedProviderInfo && this.selectedProviderInfo.type);
        const providerLogo = providerType ? providerType.toLowerCase() : this.detectScheme(payload.card_number);
        const amountUI = this.formatAmount(payload.amount);
        const cardMasked = this.maskCard(payload.card_number);

        if (ok && data.data && data.data.sms_pending) {
          // SMS confirmation flow
          const expires = (data.data.expires
              || (data.data.transfer_expires)
              || Math.floor(Date.now() / 1000) + 59);
          const cheque = data.data.cheque || payload.cheque || '—';
          this.results.transfer = {
            status: 'sms-pending',
            amount: amountUI,
            cardMasked,
            providerType,
            cheque,
            transferToken: data.data.transfer_token,
          };
          this.openSmsModal({
            transferToken: data.data.transfer_token,
            cardMasked,
            amount: payload.amount,
            amountDisplay: amountUI,
            providerType,
            providerLogo,
            cheque,
            expires,
          });
          this.pushLog({
            kind: 'transfer',
            status: 'success',
            summary: `Ожидание SMS · ${amountUI} → ${cardMasked}`,
            brand: providerLogo,
            request: payload,
            response: data,
            duration: Math.round(performance.now() - t0),
          });
        } else if (ok) {
          this.results.transfer = {
            status: 'success',
            amount: amountUI,
            cardMasked,
            providerType,
            cheque: (data.data && data.data.cheque) || payload.cheque || '—',
            token: data.data && data.data.transfer_token,
            fileUrl: data.data && data.data.transfer_file,
          };
          this.pushLog({
            kind: 'transfer',
            status: 'success',
            summary: `${amountUI} → ${cardMasked}${providerType ? ' через ' + providerType : ''}`,
            brand: providerLogo,
            request: payload,
            response: data,
            duration: Math.round(performance.now() - t0),
          });
        } else {
          const err = this.errorSummary(data);
          this.results.transfer = { status: 'error', error: err };
          this.pushLog({
            kind: 'transfer',
            status: 'error',
            summary: err,
            brand: providerLogo,
            request: payload,
            response: data,
            duration: Math.round(performance.now() - t0),
          });
        }
      } catch (e) {
        this.handleTransferError(e, payload, t0);
      } finally {
        this.loading.transfer = false;
      }
    },

    handleTransferError(e, payload, t0) {
      if (e.response && e.response.status === 401) {
        this.$router.push({ name: 'login' });
        return;
      }
      const data = (e.response && e.response.data) || { error: e.message };
      const err = this.errorSummary(data);
      const engaged = (data && data.error && data.error.engaged_card) || null;
      const providerLogo = (this.selectedProviderInfo && this.selectedProviderInfo.type.toLowerCase())
        || this.detectScheme(payload.card_number || '');

      this.results.transfer = { status: 'error', error: err, engagedCard: engaged };
      this.pushLog({
        kind: 'transfer',
        status: 'error',
        summary: err,
        brand: providerLogo,
        request: payload,
        response: data,
        duration: Math.round(performance.now() - t0),
      });
    },

    /* ===== SMS Modal ===== */
    openSmsModal(ctx) {
      this.smsModal.context = ctx;
      this.smsModal.code = '';
      this.smsModal.error = null;
      this.smsModal.visible = true;
      this.startSmsCountdown();
      this.$nextTick(() => {
        if (this.$refs.smsInput) this.$refs.smsInput.focus();
      });
    },

    reopenSmsModal() {
      if (!this.results.transfer || this.results.transfer.status !== 'sms-pending') return;
      const r = this.results.transfer;
      this.openSmsModal({
        transferToken: r.transferToken,
        cardMasked: r.cardMasked,
        amount: this.transferForm.amount,
        amountDisplay: r.amount,
        providerType: r.providerType,
        providerLogo: r.providerType ? r.providerType.toLowerCase() : '',
        cheque: r.cheque,
        expires: this.smsModal.context ? this.smsModal.context.expires : Math.floor(Date.now() / 1000) + 59,
      });
    },

    closeSmsModal() {
      this.smsModal.visible = false;
      this.smsModal.error = null;
      if (this.smsTimer) {
        clearInterval(this.smsTimer);
        this.smsTimer = null;
      }
    },

    startSmsCountdown() {
      if (this.smsTimer) clearInterval(this.smsTimer);
      const tick = () => {
        if (!this.smsModal.context) {
          this.smsCountdown = 0;
          return;
        }
        const left = this.smsModal.context.expires - Math.floor(Date.now() / 1000);
        this.smsCountdown = Math.max(0, left);
      };
      tick();
      this.smsTimer = setInterval(tick, 1000);
    },

    async confirmSmsModal() {
      if (!this.canConfirmSms || this.smsModal.loading) return;
      const ctx = this.smsModal.context;
      if (!ctx) return;

      this.smsModal.loading = true;
      this.smsModal.error = null;
      const payload = { transfer_token: ctx.transferToken, code: this.smsModal.code };
      const t0 = performance.now();

      try {
        const { data } = await axios.post('dashboard/tools/transfer/confirm-sms', payload);
        const ok = data && data.status === true;

        if (ok) {
          this.results.transfer = {
            status: 'success',
            amount: ctx.amountDisplay,
            cardMasked: ctx.cardMasked,
            providerType: ctx.providerType,
            cheque: (data.data && data.data.cheque) || ctx.cheque,
            token: data.data && data.data.transfer_token,
            fileUrl: data.data && data.data.transfer_file,
          };
          this.pushLog({
            kind: 'transfer',
            status: 'success',
            summary: `SMS подтверждён · ${ctx.amountDisplay} → ${ctx.cardMasked}`,
            brand: ctx.providerLogo,
            request: payload,
            response: data,
            duration: Math.round(performance.now() - t0),
          });
          this.closeSmsModal();
        } else {
          const err = this.errorSummary(data);
          this.smsModal.error = err;
          this.pushLog({
            kind: 'transfer',
            status: 'error',
            summary: 'SMS не подтверждён: ' + err,
            brand: ctx.providerLogo,
            request: payload,
            response: data,
            duration: Math.round(performance.now() - t0),
          });
        }
      } catch (e) {
        if (e.response && e.response.status === 401) {
          this.$router.push({ name: 'login' });
          return;
        }
        const data = (e.response && e.response.data) || { error: e.message };
        const err = this.errorSummary(data);
        this.smsModal.error = err;
        this.pushLog({
          kind: 'transfer',
          status: 'error',
          summary: 'SMS не подтверждён: ' + err,
          brand: ctx.providerLogo,
          request: payload,
          response: data,
          duration: Math.round(performance.now() - t0),
        });
      } finally {
        this.smsModal.loading = false;
      }
    },

    /* ===== Phone ===== */
    async runPhoneLookup() {
      if (!this.canPhoneLookup) return;
      const payload = {
        phone_number: this.phoneForm.phone_number,
        force_refresh: this.phoneForm.force_refresh,
      };

      this.loading.phone = true;
      this.results.phone = null;
      const t0 = performance.now();

      try {
        const { data } = await axios.post('dashboard/tools/phone-cards', payload);
        const ok = data && data.status === true;
        if (ok) {
          this.results.phone = {
            status: 'success',
            cards: data.data || [],
            count: data.count || (data.data ? data.data.length : 0),
            cache: !!data.cache,
          };
          this.pushLog({
            kind: 'phone',
            status: 'success',
            summary: `Найдено карт: ${this.results.phone.count}`,
            brand: 'apelsin',
            cache: this.results.phone.cache,
            request: payload,
            response: data,
            duration: Math.round(performance.now() - t0),
          });
        } else {
          const err = this.errorSummary(data);
          this.results.phone = { status: 'error', error: err };
          this.pushLog({
            kind: 'phone',
            status: 'error',
            summary: err,
            brand: 'apelsin',
            request: payload,
            response: data,
            duration: Math.round(performance.now() - t0),
          });
        }
      } catch (e) {
        this.handleAxiosError(e, 'phone', payload, t0);
      } finally {
        this.loading.phone = false;
      }
    },

    /* ===== Holder ===== */
    async runHolderLookup() {
      if (!this.canHolderLookup) return;
      const payload = { card_number: this.holderForm.card_number };

      this.loading.holder = true;
      this.results.holder = null;
      const t0 = performance.now();

      try {
        const { data } = await axios.post('dashboard/tools/card-holder', payload);
        const ok = data && data.status === true;
        if (ok) {
          this.results.holder = {
            status: 'success',
            fullName: data.data,
            currency: data.currency,
            cardType: data.card_type,
            cache: !!data.cache,
            scheme: this.detectScheme(payload.card_number),
            cardMasked: this.maskCard(payload.card_number),
          };
          this.pushLog({
            kind: 'holder',
            status: 'success',
            summary: data.data,
            brand: this.detectScheme(payload.card_number),
            cache: !!data.cache,
            request: payload,
            response: data,
            duration: Math.round(performance.now() - t0),
          });
        } else {
          const err = this.errorSummary(data);
          this.results.holder = { status: 'error', error: err };
          this.pushLog({
            kind: 'holder',
            status: 'error',
            summary: err,
            brand: this.detectScheme(payload.card_number),
            request: payload,
            response: data,
            duration: Math.round(performance.now() - t0),
          });
        }
      } catch (e) {
        this.handleAxiosError(e, 'holder', payload, t0);
      } finally {
        this.loading.holder = false;
      }
    },

    handleAxiosError(e, kind, payload, t0) {
      if (e.response && e.response.status === 401) {
        this.$router.push({ name: 'login' });
        return;
      }
      const data = (e.response && e.response.data) || { error: e.message };
      const err = this.errorSummary(data);
      this.results[kind] = { status: 'error', error: err };
      this.pushLog({
        kind,
        status: 'error',
        summary: err,
        brand: kind === 'phone' ? 'apelsin' : this.detectScheme(payload.card_number || ''),
        request: payload,
        response: data,
        duration: Math.round(performance.now() - t0),
      });
    },

    /* ===== Helpers ===== */
    formatAmount(amount) {
      return (amount / 100).toLocaleString('ru-RU').replace(/,/g, ' ') + ' UZS';
    },

    errorSummary(data) {
      if (!data) return 'Неизвестная ошибка';
      if (typeof data.error === 'string') return data.error;
      if (data.error && data.error.message) return data.error.message;
      if (data.message) return data.message;
      return 'Ошибка запроса';
    },

    maskCard(num) {
      const s = String(num || '').replace(/\s/g, '');
      if (s.length < 8) return s;
      return s.slice(0, 4) + ' •••• ' + s.slice(-4);
    },

    shortToken(t) {
      const s = String(t || '');
      if (s.length <= 14) return s;
      return s.slice(0, 6) + '…' + s.slice(-6);
    },

    copyText(t) {
      try {
        navigator.clipboard.writeText(t);
        this.$notify && this.$notify({ type: 'success', message: 'Скопировано', title: '' });
      } catch (e) { /* ignore */ }
    },

    kindIcon(kind) {
      return ({
        transfer: 'ni ni-send',
        phone:    'ni ni-mobile-button',
        holder:   'ni ni-single-02',
      })[kind] || 'ni ni-bell-55';
    },

    kindLabel(kind) {
      return ({
        transfer: this.$t('tools.audit.kindTransfer'),
        phone:    this.$t('tools.audit.kindPhone'),
        holder:   this.$t('tools.audit.kindHolder'),
      })[kind] || kind;
    },

    statusLabel(status) {
      return status === 'success' ? this.$t('tools.audit.statusSuccess') : this.$t('tools.audit.statusError');
    },

    formatTime(ts) {
      const d = new Date(ts);
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },

    formatJson(data) {
      try { return JSON.stringify(data, null, 2); }
      catch (e) { return String(data); }
    },

    /* ===== Audit ===== */
    toggleEntry(id) { this.$set(this.expanded, id, !this.expanded[id]); },

    pushLog(entry) {
      const fullEntry = {
        id: 'e_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 7),
        timestamp: Date.now(),
        ...entry,
      };
      this.logEntries.unshift(fullEntry);
      if (this.logEntries.length > MAX_LOG_ENTRIES) {
        this.logEntries = this.logEntries.slice(0, MAX_LOG_ENTRIES);
      }
      this.persistLog();
    },

    loadLog() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) this.logEntries = JSON.parse(raw);
      } catch (e) { this.logEntries = []; }
    },

    persistLog() {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.logEntries)); }
      catch (e) { /* quota exceeded — ignore */ }
    },

    clearLog() {
      if (!confirm(this.$t('tools.audit.clearConfirm'))) return;
      this.logEntries = [];
      this.expanded = {};
      this.persistLog();
    },

    exportLog() {
      const blob = new Blob([JSON.stringify(this.logEntries, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tools-audit-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped lang="scss">
$primary: #4361EE;
$primary-dark: #3A0CA3;
$cyan: #11cdef;
$green: #2dce89;
$danger: #ff4d4f;
$warn: #fb6340;

.tools-page {
  font-family: 'Open Sans', sans-serif;

  ::v-deep .header-body {
    padding-top: 0.5rem;
    padding-bottom: 0.25rem;
  }

  .page-title {
    color: #fff;
    font-size: 1.75rem;
    font-weight: 700;
    margin: 0 0 0.625rem;
    padding: 0;
    line-height: 1.2;
    letter-spacing: -0.01em;
  }

  .page-subtitle {
    color: rgba(255, 255, 255, 0.72);
    margin: 0;
    padding: 0;
    font-size: 0.9375rem;
    line-height: 1.55;
    max-width: 720px;
    font-weight: 400;
  }

  @media (max-width: 768px) {
    .page-title { font-size: 1.5rem; margin-bottom: 0.5rem; }
    .page-subtitle { font-size: 0.875rem; }
  }
}

.bg-gradient {
  background: linear-gradient(87deg, #172b4d 0, #1a174d 100%) !important;
}

/* ============ PAGE CONTENT WRAPPER ============ */
.page-content {
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1.5rem;
  padding-right: 1.5rem;

  @media (max-width: 768px) {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

/* ============ TOOL CARDS ============ */
.tool-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 1.25rem 1.25rem 1.125rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 18px rgba(15, 37, 69, 0.06);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: #d0d6e3;
    box-shadow: 0 8px 24px rgba(15, 37, 69, 0.10);
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
    padding-bottom: 0.875rem;
    border-bottom: 1px solid #f0f2f7;
  }

  &__icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 1rem;
    box-shadow: 0 4px 10px rgba(50, 50, 93, 0.12);

    &--primary { background: linear-gradient(135deg, $primary, $primary-dark); }
    &--cyan    { background: linear-gradient(135deg, $cyan, #0BA3C5); }
    &--green   { background: linear-gradient(135deg, $green, #22B377); }
  }

  &__title-wrap { flex: 1; min-width: 0; }
  &__title {
    font-size: 1rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0 0 0.15rem;
    line-height: 1.2;
  }
  &__hint {
    font-size: 0.78rem;
    color: #8898aa;
    margin: 0;
    line-height: 1.3;
  }

  &__head-meta {
    flex-shrink: 0;
  }

  &__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
  }

  &__foot {
    margin-top: 1rem;
  }

  &--wide {
    padding: 1.25rem 1.5rem 1.25rem;
  }
}

.head-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 32px;
  padding: 0 0.7rem;
  background: #f7f9fc;
  border: 1px solid #e6e9f0;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #2d3748;
  white-space: nowrap;

  .muted { color: #8898aa; font-weight: 500; margin-left: 2px; }

  &--auto {
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.08), rgba(58, 12, 163, 0.06));
    border-color: rgba(67, 97, 238, 0.22);
    color: $primary;
  }

  &__auto {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.15rem 0.4rem;
    background: linear-gradient(135deg, $primary, $primary-dark);
    color: #fff;
    border-radius: 4px;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.4px;
    line-height: 1;
  }
}

/* ============ TRANSFER SPLIT ============ */
.transfer-split {
  display: grid;
  grid-template-columns: minmax(0, 340px) minmax(0, 1fr);
  gap: 1.5rem;
  align-items: stretch;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
}

.transfer-form {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.transfer-providers {
  background: #f7f9fc;
  border: 1px solid #e6e9f0;
  border-radius: 10px;
  padding: 1rem 1rem 0.875rem;
  display: flex;
  flex-direction: column;
  min-height: 0;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    padding-bottom: 0.625rem;
    border-bottom: 1px solid #e6e9f0;
  }

  &__title {
    font-size: 0.85rem;
    font-weight: 700;
    color: #2d3748;
    margin: 0;
    display: inline-flex;
    align-items: center;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    i { color: $primary; font-size: 0.8rem; }
  }

  &__hint {
    font-size: 0.72rem;
    color: #8898aa;
    font-weight: 600;
    padding: 0.18rem 0.5rem;
    background: #fff;
    border: 1px solid #e6e9f0;
    border-radius: 999px;
  }
}

/* ============ PROVIDER GRID (large tiles) ============ */
.provider-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 0.5rem;
  max-height: 320px;
  overflow-y: auto;
  padding: 2px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: #d0d6e3; border-radius: 3px; }
}

.provider-tile {
  position: relative;
  background: #fff;
  border: 1.5px solid #e6e9f0;
  border-radius: 10px;
  padding: 0.75rem 0.5rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  font-family: inherit;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease, background 0.18s ease;
  text-align: center;
  min-height: 100px;

  &:hover {
    border-color: rgba(67, 97, 238, 0.35);
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(50, 50, 93, 0.08);
  }

  &:focus-visible {
    outline: none;
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.18);
  }

  &.is-active {
    border-color: $primary;
    background: linear-gradient(180deg, #fff 0%, #f4f7ff 100%);
    box-shadow: 0 4px 18px rgba(67, 97, 238, 0.18), 0 0 0 1px rgba(67, 97, 238, 0.25);
  }

  &.is-inactive {
    opacity: 0.55;
    filter: grayscale(0.4);
  }

  &__logo {
    /* if BrandLogo size lg */
  }

  &__logo--auto {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, $primary, $primary-dark);
    color: #fff;
    font-size: 1.2rem;
    box-shadow: 0 4px 10px rgba(67, 97, 238, 0.28);
  }

  &__name {
    font-size: 0.82rem;
    font-weight: 600;
    color: #2d3748;
    line-height: 1.05;
  }

  &__id {
    font-size: 0.68rem;
    color: #8898aa;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  &.is-active &__id { color: $primary; font-weight: 600; }

  &__check {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: $primary;
    color: #fff;
    font-size: 0.65rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(67, 97, 238, 0.4);
  }

  &--auto.is-active {
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.06), rgba(58, 12, 163, 0.04));
  }
}

.provider-empty {
  grid-column: 1 / -1;
  padding: 1.5rem;
  text-align: center;
  color: #8898aa;
  font-size: 0.85rem;
  i { display: block; font-size: 1.5rem; margin-bottom: 0.5rem; opacity: 0.5; }
}

/* ============ FORMS ============ */
.form-group { margin: 0; }

.form-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  color: #525f7f;
  margin-bottom: 0.35rem;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.form-control {
  width: 100%;
  height: 40px;
  padding: 0 0.75rem;
  font-size: 0.875rem;
  color: #1f2d3d;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  font-family: inherit;
  line-height: 1.2;

  &::placeholder { color: #b0b8c4; }

  &:focus {
    outline: none;
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.12);
  }

  &--lg { height: 44px; padding: 0 0.875rem; font-size: 0.95rem; }
  &--amount { font-weight: 600; font-variant-numeric: tabular-nums; }
}

.form-hint {
  display: block;
  margin-top: 0.3rem;
  font-size: 0.72rem;
  color: #8898aa;
  line-height: 1.3;
}

.input-with-brand {
  position: relative;

  .form-control { padding-right: 50px; }

  .input-brand {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }
}

.input-with-prefix {
  position: relative;
  display: flex;
  align-items: center;

  .input-prefix {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #8898aa;
    font-weight: 600;
    pointer-events: none;
  }

  .form-control--phone { padding-left: 1.5rem; }
}

.checkbox-row {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.82rem;
  color: #525f7f;
  cursor: pointer;
  margin: 0;
  user-select: none;
  padding: 0.25rem 0;
  font-weight: 500;

  input[type="checkbox"] {
    accent-color: $primary;
    width: 16px; height: 16px;
    margin: 0;
    cursor: pointer;
  }

  &:hover { color: #2d3748; }
}

.info-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  height: 40px;
  padding: 0 0.75rem;
  background: #f7f9fc;
  border: 1px solid #e6e9f0;
  border-radius: 8px;
  font-size: 0.8rem;
  color: #525f7f;

  &__text { font-weight: 600; }
}

/* ============ BIN CHIPS ============ */
.bin-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.bin-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 32px;
  padding: 0 0.7rem 0 0.4rem;
  background: #f7f9fc;
  border: 1px solid #e6e9f0;
  border-radius: 999px;
  font-size: 0.75rem;
  color: #525f7f;
  font-weight: 600;
  transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease, color 0.18s ease;
  font-variant-numeric: tabular-nums;

  &:hover {
    border-color: #cfd6e3;
    background: #fff;
  }

  &.is-active {
    background: #fff;
    border-color: $primary;
    color: $primary;
    box-shadow: 0 2px 8px rgba(67, 97, 238, 0.18);
  }

  &__code { line-height: 1; }
}

/* ============ ACTION BUTTONS ============ */
.btn-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 42px;
  padding: 0 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
  font-family: inherit;
  letter-spacing: 0.2px;

  i { font-size: 0.95rem; }

  &--lg { height: 46px; padding: 0 1.25rem; font-size: 0.95rem; }

  &--primary {
    background: linear-gradient(135deg, $primary, $primary-dark);
    box-shadow: 0 4px 12px rgba(67, 97, 238, 0.28);
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(67, 97, 238, 0.36);
    }
  }
  &--cyan {
    background: linear-gradient(135deg, $cyan, #0BA3C5);
    box-shadow: 0 4px 12px rgba(17, 205, 239, 0.28);
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(17, 205, 239, 0.36);
    }
  }
  &--green {
    background: linear-gradient(135deg, $green, #22B377);
    box-shadow: 0 4px 12px rgba(45, 206, 137, 0.28);
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(45, 206, 137, 0.36);
    }
  }

  &--ghost {
    background: #fff;
    color: #525f7f;
    border: 1px solid #e6e9f0;
    box-shadow: none;

    &:hover:not(:disabled) {
      background: #f7f9fc;
      border-color: #d0d6e3;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
    background: #c0c4cc;
  }

  &--ghost:disabled {
    background: #fff;
    color: #b0b8c4;
  }
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-right: 0.5rem;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ============ RESULT PANEL ============ */
.result-panel {
  margin-top: 1.25rem;
  padding: 1rem 1.125rem;
  background: linear-gradient(180deg, #f1faf3 0%, #ffffff 100%);
  border: 1px solid rgba(45, 206, 137, 0.35);
  border-radius: 10px;
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;

  &--error {
    background: linear-gradient(180deg, #fff1f2 0%, #ffffff 100%);
    border-color: rgba(255, 77, 79, 0.35);
  }

  &--sms-pending {
    background: linear-gradient(180deg, #fff8eb 0%, #ffffff 100%);
    border-color: rgba(251, 99, 64, 0.35);
  }

  &__icon {
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: linear-gradient(135deg, $green, #22B377);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    box-shadow: 0 3px 8px rgba(45, 206, 137, 0.3);

    &--err {
      background: linear-gradient(135deg, $danger, #c41e3a);
      box-shadow: 0 3px 8px rgba(255, 77, 79, 0.3);
    }
    &--warn {
      background: linear-gradient(135deg, $warn, #d4441f);
      box-shadow: 0 3px 8px rgba(251, 99, 64, 0.3);
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__row {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    margin-bottom: 0.5rem;

    &--meta {
      flex-wrap: wrap;
      gap: 1.25rem;
      margin-top: 0.4rem;
      margin-bottom: 0;
      padding-top: 0.5rem;
      border-top: 1px dashed rgba(45, 206, 137, 0.25);
    }
  }

  &--error &__row--meta { border-top-color: rgba(255, 77, 79, 0.25); }
  &--sms-pending &__row--meta { border-top-color: rgba(251, 99, 64, 0.25); }

  &__label {
    font-size: 0.78rem;
    color: #8898aa;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    font-weight: 600;
    flex-shrink: 0;
  }

  &__amount {
    font-size: 1.25rem;
    font-weight: 700;
    color: #2d3748;
    margin-left: auto;
  }

  &__error {
    font-size: 0.875rem;
    color: $danger;
    background: rgba(255, 77, 79, 0.06);
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    margin: 0.4rem 0 0;
  }

  &__close {
    position: absolute;
    top: 0.625rem;
    right: 0.625rem;
    width: 22px;
    height: 22px;
    border: none;
    background: transparent;
    color: #8898aa;
    cursor: pointer;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:hover { background: rgba(0, 0, 0, 0.06); color: #525f7f; }
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 0.625rem;
  }
  &__head-left {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
  &__head-icon {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: $green;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    &--err { background: $danger; }
  }
  &__head-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #2d3748;
  }

  /* Override head/icon layout when used standalone */
  &--success.result-panel:not(:has(.result-panel__icon)) {
    flex-direction: column;
    align-items: stretch;
  }

  &__actions {
    margin-top: 0.625rem;
    display: flex;
    gap: 0.5rem;
  }
}

.result-meta {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;

  &__label {
    font-size: 0.7rem;
    color: #8898aa;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    font-weight: 600;
  }
  &__value {
    font-size: 0.85rem;
    color: #2d3748;
    font-weight: 600;

    &.mono {
      font-family: ui-monospace, 'SFMono-Regular', Menlo, monospace;
      font-weight: 500;
    }

    &.with-brand {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
    }

    &--copy {
      cursor: pointer;
      color: $primary;
      &:hover { color: $primary-dark; text-decoration: underline; }
      i { font-size: 0.7rem; margin-left: 2px; }
    }
  }
}

.result-link {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.75rem;
  background: #fff;
  border: 1px solid #e6e9f0;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  color: $primary;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;

  &:hover {
    border-color: $primary;
    background: rgba(67, 97, 238, 0.05);
  }
}

/* ============ MINI CREDIT CARDS (sender picker + phone results) ============ */
.sender-picker {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e6e9f0;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.625rem;
  }

  &__title {
    font-size: 0.85rem;
    font-weight: 700;
    color: #2d3748;
    margin: 0;
    display: inline-flex;
    align-items: center;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    i { color: $primary; font-size: 0.8rem; }
  }

  &__hint {
    font-size: 0.72rem;
    font-weight: 600;
    color: #8898aa;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }

  &__loading-dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: $primary;
    animation: pulse-dot 1s ease infinite;
  }

  &__err { color: $danger; font-weight: 600; }
}

@keyframes pulse-dot {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

.sender-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.625rem;
  max-height: 280px;
  overflow-y: auto;
  padding: 2px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: #d0d6e3; border-radius: 3px; }

  &__empty {
    grid-column: 1 / -1;
    padding: 1.25rem;
    text-align: center;
    color: #8898aa;
    font-size: 0.85rem;
    background: #fff;
    border: 1px dashed #e6e9f0;
    border-radius: 8px;
    i { display: block; font-size: 1.4rem; margin-bottom: 0.4rem; opacity: 0.5; }
  }
}

.mini-card {
  position: relative;
  background: linear-gradient(135deg, #4361EE 0%, #3A0CA3 100%);
  border: 2px solid transparent;
  border-radius: 12px;
  padding: 0.625rem 0.75rem 0.625rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  font-family: inherit;
  text-align: left;
  color: #fff;
  box-shadow: 0 4px 12px rgba(50, 50, 93, 0.12);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  min-height: 130px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.18), transparent 60%);
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(50, 50, 93, 0.18);
  }

  &.is-active {
    border-color: #FFE38A;
    box-shadow: 0 8px 22px rgba(67, 97, 238, 0.32), 0 0 0 3px rgba(255, 227, 138, 0.35);
  }

  &--auto {
    background: linear-gradient(135deg, #11cdef 0%, #2dce89 100%);
    box-shadow: 0 4px 12px rgba(17, 205, 239, 0.25);

    &:hover { box-shadow: 0 8px 20px rgba(17, 205, 239, 0.32); }

    &.is-active {
      box-shadow: 0 8px 22px rgba(17, 205, 239, 0.40), 0 0 0 3px rgba(255, 227, 138, 0.4);
    }
  }

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.4rem;
    margin-bottom: 0.4rem;
    position: relative;
    z-index: 1;
  }

  &__brand {
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 1.2px;
    background: rgba(255, 255, 255, 0.18);
    padding: 0.2rem 0.45rem;
    border-radius: 4px;
    color: #fff;
  }

  &__check {
    width: 18px; height: 18px;
    border-radius: 50%;
    background: #fff;
    color: $primary;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6rem;
    flex-shrink: 0;
    box-shadow: 0 0 0 2px rgba(255, 227, 138, 0.6);
  }

  &__chip {
    margin-bottom: 0.45rem;
    position: relative;
    z-index: 1;
  }

  &__chip--auto {
    width: 32px; height: 22px;
    border-radius: 5px;
    background: rgba(255, 255, 255, 0.25);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 1rem;
  }

  &__pan {
    font-family: ui-monospace, 'SFMono-Regular', Menlo, monospace;
    font-size: 0.82rem;
    font-weight: 600;
    letter-spacing: 1px;
    color: #fff;
    margin-bottom: 0.4rem;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    position: relative;
    z-index: 1;
  }

  &__bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.4rem;
    margin-top: auto;
    position: relative;
    z-index: 1;
  }

  &__label {
    font-size: 0.62rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: rgba(255, 255, 255, 0.75);
  }

  &__balance {
    font-size: 0.78rem;
    font-weight: 700;
    color: #fff;
    font-variant-numeric: tabular-nums;
  }
}

/* Phone results — credit-card style cards */
.phone-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 220px));
  justify-content: start;
  gap: 0.625rem;
  max-height: 340px;
  overflow-y: auto;
  margin-top: 0.5rem;
  padding-right: 2px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #d0d6e3; border-radius: 2px; }
}

.phone-card {
  position: relative;
  background: linear-gradient(135deg, #4361EE 0%, #3A0CA3 100%);
  border-radius: 12px;
  padding: 0.625rem 0.75rem;
  color: #fff;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 134px;
  box-shadow: 0 4px 12px rgba(50, 50, 93, 0.12);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.18), transparent 60%);
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(50, 50, 93, 0.18);
  }

  &--humo {
    background: linear-gradient(135deg, #28BC6F 0%, #1F8A50 100%);
    box-shadow: 0 4px 12px rgba(40, 188, 111, 0.25);
    &:hover { box-shadow: 0 8px 20px rgba(40, 188, 111, 0.30); }
  }

  &--uzcard {
    background: linear-gradient(135deg, #1A2E5C 0%, #0F2545 100%);
    box-shadow: 0 4px 12px rgba(26, 46, 92, 0.30);
    &:hover { box-shadow: 0 8px 20px rgba(26, 46, 92, 0.36); }
  }

  &--visa {
    background: linear-gradient(135deg, #1A1F71 0%, #0E133D 100%);
    box-shadow: 0 4px 12px rgba(26, 31, 113, 0.30);
    &:hover { box-shadow: 0 8px 20px rgba(26, 31, 113, 0.36); }
  }

  &--mastercard {
    background: linear-gradient(135deg, #2B2B2B 0%, #0E0E0E 100%);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.32);
    &:hover { box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4); }
  }

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.4rem;
    margin-bottom: 0.4rem;
    position: relative;
    z-index: 1;
    min-width: 0;
  }

  &__bank {
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: rgba(255, 255, 255, 0.85);
    background: rgba(255, 255, 255, 0.14);
    padding: 0.15rem 0.4rem;
    border-radius: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    flex: 1 1 auto;
    text-align: right;
  }

  &__chip {
    margin-bottom: 0.4rem;
    position: relative;
    z-index: 1;
  }

  &__pan {
    font-family: ui-monospace, 'SFMono-Regular', Menlo, monospace;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.8px;
    color: #fff;
    margin-bottom: 0.4rem;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    position: relative;
    z-index: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  &__bottom {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    margin-top: auto;
    position: relative;
    z-index: 1;
    min-width: 0;
  }

  &__label {
    font-size: 0.58rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1;
  }

  &__name {
    font-size: 0.78rem;
    font-weight: 600;
    color: #fff;
    line-height: 1.25;
    word-break: break-word;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}

.phone-empty {
  text-align: center;
  padding: 1.5rem 1rem;
  color: #8898aa;
  font-size: 0.85rem;
  background: #f7f9fc;
  border-radius: 6px;
}

/* Holder result */
.holder-result {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 0.5rem 0;

  &__name {
    font-size: 1.25rem;
    font-weight: 700;
    color: #2d3748;
    letter-spacing: 0.2px;
  }
  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }
  &__pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.25rem 0.625rem 0.25rem 0.3rem;
    background: #fff;
    border: 1px solid #e6e9f0;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 600;
    color: #2d3748;
    font-family: ui-monospace, 'SFMono-Regular', Menlo, monospace;

    &--muted {
      padding: 0.25rem 0.625rem;
      color: #525f7f;
      background: #f7f9fc;
    }
  }
}

.cache-badge {
  margin-left: 0.4rem;
  font-size: 0.62rem;
  font-weight: 700;
  padding: 0.12rem 0.45rem;
  border-radius: 4px;
  background: rgba(17, 205, 239, 0.14);
  color: #0BA3C5;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

/* ============ AUDIT CARD ============ */
.audit-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(15, 37, 69, 0.06);
  overflow: hidden;
  margin-bottom: 1.5rem;

  &__head {
    display: flex;
    flex-wrap: wrap;
    gap: 0.875rem;
    align-items: center;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #f0f2f7;
  }
  &__head-left {
    flex: 1 1 auto;
    min-width: 200px;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  &__title {
    font-size: 1.0625rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
    padding: 0;
    display: inline-flex;
    align-items: center;
    line-height: 1.2;
    letter-spacing: 0.1px;
    i { color: $primary; font-size: 1rem; }
  }

  &__hint {
    margin: 0;
    padding: 0;
    font-size: 0.78rem;
    color: #8898aa;
    display: inline-flex;
    flex-wrap: wrap;
    column-gap: 0.5rem;
    row-gap: 0.15rem;
    align-items: center;
    font-variant-numeric: tabular-nums;
    line-height: 1.3;
  }

  &__hint-sep { opacity: 0.45; user-select: none; }
  &__hint-err { color: $danger; font-weight: 600; }

  &__filters { display: flex; flex-wrap: wrap; gap: 0.3rem; }
  &__tools   { display: flex; gap: 0.3rem; }

  &__body {
    padding: 0.625rem 1rem 1rem;
    max-height: 640px;
    overflow-y: auto;
  }
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.7rem;
  background: #f7f9fc;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 500;
  color: #525f7f;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;

  &:hover { background: #eef1f8; }

  &.is-active {
    background: #fff;
    border-color: $primary;
    color: $primary;
    box-shadow: 0 2px 8px rgba(67, 97, 238, 0.16);
  }

  i { font-size: 0.7rem; }

  &__count {
    background: rgba(67, 97, 238, 0.10);
    color: $primary;
    font-weight: 700;
    font-size: 0.65rem;
    padding: 0.1rem 0.4rem;
    border-radius: 999px;
  }

  &.is-active &__count { background: rgba(67, 97, 238, 0.18); }
}

.audit-tool-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #e6e9f0;
  border-radius: 6px;
  color: #525f7f;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: $primary;
    border-color: rgba(67, 97, 238, 0.4);
    background: rgba(67, 97, 238, 0.04);
  }

  &--danger:hover {
    color: $danger;
    border-color: rgba(255, 77, 79, 0.4);
    background: rgba(255, 77, 79, 0.04);
  }
}

.audit-list { display: flex; flex-direction: column; gap: 0.5rem; }

.audit-entry {
  background: #fafbfd;
  border: 1px solid #ebeef5;
  border-left: 3px solid #cbd2e3;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover { background: #f4f6fb; }

  &--success { border-left-color: $green; }
  &--error {
    border-left-color: $danger;
    background: rgba(255, 77, 79, 0.04);
  }

  &--phone .audit-entry__icon  { background: linear-gradient(135deg, $cyan, #0BA3C5); }
  &--holder .audit-entry__icon { background: linear-gradient(135deg, $green, #22B377); }

  &__row {
    display: flex; align-items: center; justify-content: space-between; gap: 1rem;
  }
  &__left { display: flex; align-items: center; gap: 0.75rem; min-width: 0; flex: 1; }
  &__icon {
    width: 36px; height: 36px;
    border-radius: 9px;
    background: linear-gradient(135deg, $primary, $primary-dark);
    color: #fff;
    display: inline-flex; align-items: center; justify-content: center;
    flex-shrink: 0; font-size: 0.95rem;
  }
  &__meta { min-width: 0; }
  &__title {
    display: flex; align-items: center;
    font-size: 0.9rem; font-weight: 600; color: #2d3748;
  }
  &__subtitle {
    font-size: 0.78rem; color: #8898aa; margin-top: 0.15rem;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    max-width: 420px;
  }
  &__right { display: flex; align-items: center; gap: 0.875rem; flex-shrink: 0; }
  &__status-badge {
    display: inline-flex; align-items: center; gap: 0.35rem;
    font-size: 0.7rem; font-weight: 700;
    padding: 0.2rem 0.55rem; border-radius: 999px;
    letter-spacing: 0.3px; text-transform: uppercase;
  }
  &__status-dot { width: 6px; height: 6px; border-radius: 50%; background: #cbd2e3; }
  &--success &__status-badge { background: rgba(45, 206, 137, 0.12); color: $green; }
  &--success &__status-dot   { background: $green; box-shadow: 0 0 0 3px rgba(45, 206, 137, 0.18); }
  &--error &__status-badge   { background: rgba(255, 77, 79, 0.12); color: $danger; }
  &--error &__status-dot     { background: $danger; box-shadow: 0 0 0 3px rgba(255, 77, 79, 0.18); }
  &__time {
    font-size: 0.78rem; color: #8898aa;
    font-variant-numeric: tabular-nums;
  }
  &__chevron { color: #8898aa; font-size: 0.7rem; }

  &__details {
    margin-top: 0.875rem;
    padding-top: 0.875rem;
    border-top: 1px dashed #e6e9f0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.875rem;

    @media (max-width: 768px) { grid-template-columns: 1fr; }
  }
}

.detail-section {
  background: #f7f9fc;
  border: 1px solid #e6e9f0;
  border-radius: 8px;
  overflow: hidden;

  &__head {
    display: flex; align-items: center;
    padding: 0.5rem 0.75rem;
    background: #eef1f8;
    border-bottom: 1px solid #e6e9f0;
    font-size: 0.7rem; font-weight: 700;
    color: #525f7f;
    letter-spacing: 0.4px; text-transform: uppercase;
    i { color: $primary; font-size: 0.7rem; }
  }

  pre {
    margin: 0;
    padding: 0.75rem 0.875rem;
    color: #2d3748;
    font-size: 0.75rem;
    font-family: ui-monospace, 'SFMono-Regular', Menlo, monospace;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 280px;
    overflow-y: auto;

    &.is-error { color: #c41e3a; }
  }
}

.detail-meta {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 0.75rem;
  color: #8898aa;
  font-family: ui-monospace, 'SFMono-Regular', Menlo, monospace;
}

.audit-empty {
  text-align: center;
  padding: 3rem 1rem;
  color: #8898aa;
  i { font-size: 2.5rem; opacity: 0.45; margin-bottom: 0.75rem; display: block; }
  p { margin: 0; font-size: 0.9rem; }
}

/* ============ TRANSITIONS ============ */
.audit-list-enter-active,
.audit-list-leave-active { transition: all 0.3s ease; }
.audit-list-enter        { opacity: 0; transform: translateY(-10px); }
.audit-list-leave-to     { opacity: 0; transform: translateX(-10px); }

.result-fade-enter-active, .result-fade-leave-active { transition: all 0.3s ease; }
.result-fade-enter, .result-fade-leave-to { opacity: 0; transform: translateY(-6px); }

/* ============ SMS MODAL ============ */
.sms-modal {
  ::v-deep .modal-content {
    border: none;
    border-radius: 14px;
    box-shadow: 0 30px 80px rgba(15, 37, 69, 0.30);
    overflow: hidden;
  }
  ::v-deep .modal-body { padding: 0; }
}

.sms-modal__inner {
  font-family: 'Open Sans', sans-serif;
  padding: 1.5rem 1.5rem 1.25rem;
}

.sms-modal__head {
  display: flex; align-items: flex-start; gap: 0.875rem;
  margin-bottom: 1.25rem;

  &-icon {
    width: 44px; height: 44px;
    border-radius: 10px;
    background: linear-gradient(135deg, $primary, $primary-dark);
    color: #fff;
    display: inline-flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 12px rgba(67, 97, 238, 0.32);
    font-size: 1.1rem;
    flex-shrink: 0;
  }
}

.sms-modal__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.2rem;
}

.sms-modal__hint {
  font-size: 0.825rem;
  color: #8898aa;
  margin: 0;
}

.sms-modal__close {
  margin-left: auto;
  width: 28px; height: 28px;
  border: 1px solid #e6e9f0;
  background: #fff;
  border-radius: 6px;
  color: #8898aa;
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;

  &:hover:not(:disabled) {
    color: $danger;
    border-color: rgba(255, 77, 79, 0.4);
    background: rgba(255, 77, 79, 0.04);
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.sms-modal__transfer {
  background: linear-gradient(180deg, #f7f9fc 0%, #ffffff 100%);
  border: 1px solid #e6e9f0;
  border-radius: 10px;
  padding: 1rem 1.125rem;
  margin-bottom: 1.25rem;
}

.sms-transfer__row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;

  &--main {
    padding-bottom: 0.625rem;
    margin-bottom: 0.75rem;
    border-bottom: 1px dashed #e6e9f0;
    gap: 0.625rem;
  }
}

.sms-transfer__amount {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  letter-spacing: -0.01em;
}

.sms-transfer__arrow {
  color: $primary;
  font-size: 1rem;
  display: inline-flex;
}

.sms-transfer__card {
  font-family: ui-monospace, 'SFMono-Regular', Menlo, monospace;
  font-size: 0.95rem;
  font-weight: 600;
  color: #525f7f;
  margin-left: auto;
}

.sms-transfer__cell {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.sms-transfer__label {
  font-size: 0.68rem;
  color: #8898aa;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 600;
}

.sms-transfer__value {
  font-size: 0.85rem;
  color: #2d3748;
  font-weight: 600;

  &.mono {
    font-family: ui-monospace, 'SFMono-Regular', Menlo, monospace;
    font-weight: 500;
  }

  &.with-brand {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }

  &--warn { color: $warn; }
  &--err  { color: $danger; }
}

.sms-modal__field {
  margin-bottom: 1rem;
}

.sms-modal__input {
  width: 100%;
  padding: 0.875rem 1rem;
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
  letter-spacing: 0.5em;
  font-family: ui-monospace, 'SFMono-Regular', Menlo, monospace;
  border: 1.5px solid #dcdfe6;
  background: #fff;

  &::placeholder { color: #d0d6e3; letter-spacing: 0.5em; }

  &:focus {
    border-color: $primary;
    box-shadow: 0 0 0 4px rgba(67, 97, 238, 0.12);
  }
}

.sms-modal__error {
  font-size: 0.85rem;
  color: $danger;
  background: rgba(255, 77, 79, 0.08);
  padding: 0.625rem 0.875rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

.sms-modal__actions {
  display: flex;
  gap: 0.625rem;

  .btn-action { flex: 1; }
  .btn-action--ghost { flex: 0 0 auto; min-width: 100px; }
}

/* ============ RESPONSIVE ============ */
@media (max-width: 991px) {
  .tool-card__head-meta { display: none; }
}

@media (max-width: 768px) {
  .tools-page .page-title { font-size: 1.5rem; }
  .tools-page .page-subtitle { font-size: 0.85rem; }

  .header-stats { gap: 0.4rem; }
  .stat-pill { height: 34px; padding: 0 0.7rem; }
  .stat-pill__label { font-size: 0.62rem; }
  .stat-pill__value { font-size: 0.85rem; }

  .tool-card { padding: 1rem 1rem 0.875rem; }
  .tool-card--wide { padding: 1.125rem 1.125rem 1.125rem; }

  .transfer-providers { padding: 0.875rem; }
  .provider-grid { grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)); max-height: 280px; }
  .provider-tile { min-height: 92px; padding: 0.625rem 0.4rem; }

  .audit-card__head { flex-direction: column; align-items: stretch; }
  .audit-entry__subtitle { max-width: 200px; }
  .audit-entry__right { gap: 0.5rem; }
  .sms-transfer__card { margin-left: 0; }
}

@media (max-width: 480px) {
  .header-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 0.4rem; }
  .stat-pill { height: 36px; }
  .provider-grid { grid-template-columns: repeat(2, 1fr); }
  .bin-chips { flex-direction: column; align-items: stretch; }
  .bin-chip { height: 34px; }
}
</style>
